import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';

import { GeocodePrecision } from '../common/Geo';
import { ApiServiceBase } from './ApiServiceBase';

/** `GET api/geo/place`: a typed place as a coordinate, never finer than its centroid. */
export interface GeoPlace {
  latitude: number;
  longitude: number;
  precision: GeocodePrecision;
}

/** `GET api/geo/my-location`: the signed-in user's own customer address, geocoded. */
export interface CustomerLocation {
  latitude: number;
  longitude: number;
  geocodePrecision: GeocodePrecision;
}

/**
 * Backend #678: the two location sources of the fallback chain that need the backend. The
 * device position (step 1) never reaches it as anything but the rounded query parameters of a
 * located search.
 */
@Injectable({ providedIn: 'root' })
export class GeoService extends ApiServiceBase {
  protected readonly path = 'geo';

  /**
   * A postcode and/or a city as a coordinate, through the backend's geocoder under a PostalCode
   * ceiling. Anonymous. **404** when the geocoder knows no such place, **400 `geoPlaceRequired`**
   * when neither was given — the caller words the first, the dictionary the second.
   *
   * Empty values are left out rather than sent as empty strings, so the backend sees exactly
   * what the user typed.
   */
  place(postalCode: string | null | undefined, city: string | null | undefined, countryCode2?: string): Observable<GeoPlace> {
    const params: Record<string, string> = {};
    if (postalCode?.trim()) {
      params['postalCode'] = postalCode.trim();
    }
    if (city?.trim()) {
      params['city'] = city.trim();
    }
    if (countryCode2?.trim()) {
      params['countryCode2'] = countryCode2.trim();
    }
    return this.http.get<GeoPlace>(this.getUrl() + '/place', { params });
  }

  /**
   * The own customer's Resolved position — fallback step 2, which exists because buyers are
   * geocoded too. Needs shop access; **404** while the customer has no Resolved coordinate.
   */
  myLocation(): Observable<CustomerLocation> {
    return this.http.get<CustomerLocation>(this.getUrl() + '/my-location');
  }
}
